import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import RefreshButton from "components/financials/modern/RefreshButton";
import SelectFinancialsCurrency from "components/financials/modern/SelectFinancialsCurrency";
import SelectIndicatorsDataSource from "components/financials/modern/SelectIndicatorsDataSource";
import SelectStatementType from "components/financials/modern/SelectStatementType";
import TextAlignmentRadioGroup from "components/financials/modern/TextAlignmentRadioGroup";
import ToggleSortingButton from "components/financials/modern/ToggleSortingButton";
import { useSpecificSetting } from "components/settings/hooks/useSpecificSetting";
import classNames from "components/ui/classNames";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/Dialog";
import { Separator } from "components/ui/Separator";
import useTheme from "components/ui/useTheme";
import { SettingsIcon } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { IFinancialsStatementType, IIndicatorDataSource } from "types/graphqlTypes";

interface SettingsDialogProps {
  className?: string;
  loading: boolean;
  textAlignment: "LEFT" | "CENTER" | "RIGHT";
  sortDirection: "ASC" | "DESC";
  currency: string;

  onChangeCurrency: (currency: string) => void;
  onChangeTextAlignment: (value: "LEFT" | "CENTER" | "RIGHT") => void;
  onToggleSorting: () => void;
  onClickRefetch?: () => void;
  onChangeStatementType: (statementType: IFinancialsStatementType) => void;
  onChangeDataSource: (dataSoruce: IIndicatorDataSource) => void;
}
export default function SettingsDialog({
  className,
  loading,
  sortDirection,
  textAlignment,
  currency,

  onToggleSorting,
  onChangeTextAlignment,
  onClickRefetch,
  onChangeCurrency,
  onChangeStatementType,
  onChangeDataSource,
}: SettingsDialogProps) {
  const theme = useTheme();
  const intl = useIntl();
  const issuer = useRootIssuer();

  const isFiisIssuer = issuer.__typename === "BrazilianRealEstateFund";
  const isBrazilianIssuer = issuer.__typename === "BrazilianCompanyIssuer";

  const { value: statementTypeFromSettings, set: setStatementTypeSetting } = useSpecificSetting(
    "financialsTable.statementType",
    IFinancialsStatementType.Annual,
  );
  const { value: indicatorDataSourceFromSettings, set: setIndicatorDataSourceSetting } = useSpecificSetting(
    "financialsTable.indicatorDataSource",
    IIndicatorDataSource.Cvm,
  );

  return (
    <Dialog>
      <DialogTrigger
        className={classNames(
          "rounded-xl bg-slate-50/60 p-2",
          "border border-slate-200 text-sm font-medium tracking-wide text-slate-600",
          "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
          "focus-visible:outline-none",
          "focus-visible:ring-1 focus-visible:ring-offset-1",
          "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
          "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
          "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
          "hover:bg-slate-100 hover:dark:bg-slate-600",
          className,
        )}
      >
        <SettingsIcon />
      </DialogTrigger>

      <DialogContent
        className={classNames({
          "border-slate-900/80 bg-slate-800 shadow-neutral-950/40": theme === "dark",
          dark: theme === "dark",
        })}
        onOpenAutoFocus={(event) => {
          // See: https://github.com/radix-ui/primitives/discussions/935
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage defaultMessage="Configurações" id="S+b9+I" />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage defaultMessage="Personalize a exibição dos dados" id="+KdRBp" />
          </DialogDescription>
        </DialogHeader>

        <SettingsItem
          title={intl.formatMessage({ defaultMessage: "Visualização dos dados", id: "RfO/Af" })}
          description={intl.formatMessage({
            defaultMessage: "Personalize a exibição dos dados",
            id: "+KdRBp",
          })}
        >
          <ToggleSortingButton sortDirection={sortDirection} onClick={() => onToggleSorting()} />

          <TextAlignmentRadioGroup value={textAlignment} onChange={onChangeTextAlignment} />

          <SelectFinancialsCurrency disabled={loading} value={currency || "USD"} onChange={onChangeCurrency} />

          {onClickRefetch && <RefreshButton loading={loading} onClick={onClickRefetch} />}
        </SettingsItem>

        {!isFiisIssuer && (
          <>
            <Separator />
            <SettingsItem
              title={intl.formatMessage({ defaultMessage: "Tipo de balanço", id: "XCZGUS" })}
              description={intl.formatMessage({
                defaultMessage:
                  "Selecione o tipo de balanço padrão que deseja visualizar ao carregar a tabela pela primeira vez",
                id: "/dw3Vn",
              })}
            >
              <SelectStatementType
                disabled={loading}
                statementType={
                  statementTypeFromSettings === IFinancialsStatementType.Quarterly
                    ? IFinancialsStatementType.Quarterly
                    : IFinancialsStatementType.Annual
                }
                onChangeStatementType={(value) => {
                  if (value === IFinancialsStatementType.Annual || value === IFinancialsStatementType.Quarterly) {
                    setStatementTypeSetting(value);
                    onChangeStatementType(value);
                  }
                }}
              />
            </SettingsItem>
          </>
        )}

        {isBrazilianIssuer && (
          <>
            <Separator />
            <SettingsItem
              title={intl.formatMessage({ defaultMessage: "Fonte de dados", id: "2+fdTL" })}
              description={intl.formatMessage({
                defaultMessage:
                  "Selecione a fonte de dados padrão que deseja visualizar ao carregar a tabela pela primeira vez",
                id: "w33yKp",
              })}
            >
              <SelectIndicatorsDataSource
                value={
                  indicatorDataSourceFromSettings === IIndicatorDataSource.Morningstar
                    ? IIndicatorDataSource.Morningstar
                    : IIndicatorDataSource.Cvm
                }
                onChange={(value) => {
                  if (value === IIndicatorDataSource.Cvm || value === IIndicatorDataSource.Morningstar) {
                    setIndicatorDataSourceSetting(value);
                    onChangeDataSource(value);
                  }
                }}
              />
            </SettingsItem>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface SettingsItemProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

function SettingsItem({ title, description, children }: SettingsItemProps) {
  return (
    <div className="flex flex-col space-y-2">
      <div>
        <h3>{title}</h3>

        <span className="text-sm text-slate-400 dark:text-slate-500">{description}</span>
      </div>

      <div className="flex w-fit gap-4">{children}</div>
    </div>
  );
}
